import { VStack, StackProps, Box } from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef } from "react";
import { Except } from "type-fest";
import { useHasVirtualKeyboard } from "../../../hooks/useHasVirtualKeyboard";
import { OptionContentComponent } from "./OptionItem";
import { AnySelectOption } from "./SelectOption";
import { SelectorBulkActions } from "./SelectorBulkActions";
import { SelectorList } from "./SelectorList";
import { SelectorSearch } from "./SelectorSearch";
import { useGridRovingTabIndex } from "./useGridRovingTabIndex";
import { useSelectOptions } from "./useSelectOptions";

interface SelectorContainerProps<TOption extends AnySelectOption>
  extends Except<StackProps, "onChange"> {
  options: TOption[];
  selectedValues: TOption["value"][];
  searchOptions: TOption[];
  searchValue: string;
  setSearchValue: (value: string) => void;
  onChange: (values: TOption["value"][]) => void;

  // config options
  enableMultiSelect: boolean;
  enableDoubleColumn: boolean; // use two columns in the dropdown
  enableSearch: boolean; // enable a search box
  enableBulkSelect: boolean;
  enableDeselect: boolean;
  moveSelectedToTop: boolean; // selected options go to the top of the list

  // state
  isLoading?: boolean;

  // styling
  wrapText: boolean;

  // Supply a component for visually rendering the Option
  OptionContent: OptionContentComponent<TOption>;
}

export function SelectorContainer<TOption extends AnySelectOption>({
  options,
  selectedValues,
  searchOptions,
  searchValue,
  setSearchValue,
  enableMultiSelect,
  enableDoubleColumn,
  enableSearch,
  enableBulkSelect,
  enableDeselect,
  moveSelectedToTop,
  onChange,
  isLoading,
  wrapText,
  OptionContent,
  ...props
}: SelectorContainerProps<TOption>): React.ReactElement {
  const searchRef = useRef<HTMLInputElement>(null);

  const selector = useSelectOptions({
    allOptions: options,
    visibleOptions: searchOptions,
    selectedValues,
    onChange,
    enableMultiSelect,
    enableDeselect,
    moveSelectedToTop,
  });

  const focusSearchInput = useCallback(() => {
    searchRef.current?.focus();
  }, []);

  const {
    activeValue,
    focusIndex,
    containerRef,
    handleContainerKeyDown,
    handleContainerFocus,
  } = useGridRovingTabIndex({
    values: selector.sortedOptionValues,
    columnCount: enableDoubleColumn ? 2 : 1,
    onActivate: selector.selectOption,
    onFinalArrowUp: focusSearchInput,
  });

  // allow arrow down to move from the search input to the first option.
  const handleSelectorSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          focusIndex(0);
        }
      },
      [focusIndex],
    );

  // avoid the virtual keyboard appearing on mobile devices due to auto-focus.
  const hasVirtualKeyboard = useHasVirtualKeyboard();

  // on the first open, we send focus into the search bar or the first option
  const hasAutoFocusedRef = useRef(false);
  useEffect(() => {
    if (hasAutoFocusedRef.current) {
      return;
    }

    // if search input focus is allowed, then we immediately send focus so the
    // user can type, even while the options are continuing to load.
    if (enableSearch && !hasVirtualKeyboard) {
      setTimeout(() => searchRef.current?.focus(), 0);
      hasAutoFocusedRef.current = true;
      return;
    }

    // if search input should not be focused, then we wait until there is at
    // least one option, so we can send focus to it.
    if (selector.sortedOptions.length > 0) {
      hasAutoFocusedRef.current = true;

      // If there is exactly one item already selected, then we send focus to it.
      // The user will naturally have their attention drawn to the already
      // selected item, so it helps for UX.
      const selectedIndex =
        selectedValues.length === 1 ?
          selector.sortedOptionValues.indexOf(selectedValues[0])
        : -1;

      focusIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [
    selectedValues,
    selector.sortedOptionValues,
    enableSearch,
    selector.sortedOptions.length,
    focusIndex,
    hasVirtualKeyboard,
  ]);

  return (
    <VStack
      alignItems="flex-start"
      spacing={0}
      flexGrow={1}
      flexShrink={1}
      overflowX="hidden"
      {...props}
    >
      {enableSearch && (
        <Box
          w="full"
          px={2}
          py={2}
          borderColor="gray.300"
          borderBottomWidth="1px"
        >
          <SelectorSearch
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchRef={searchRef}
            onSearchKeyDown={handleSelectorSearchKeyDown}
          />
        </Box>
      )}

      <SelectorList
        options={options}
        sortedOptions={selector.sortedOptions}
        selectedValueSet={selector.selectedValueSet}
        activeValue={activeValue}
        isLoading={isLoading}
        enableDoubleColumn={enableDoubleColumn}
        wrapText={wrapText}
        containerRef={containerRef}
        onFocus={handleContainerFocus}
        onKeyDown={handleContainerKeyDown}
        onSelect={selector.selectOption}
        OptionContent={OptionContent}
      />

      {enableBulkSelect && (
        <SelectorBulkActions
          selectedCount={selectedValues.length}
          totalCount={options.length}
          filteredCount={searchOptions.length}
          hasUnselectedVisibleOptions={selector.hasUnselectedVisibleOptions}
          onSelectVisible={selector.selectVisible}
          onClearAll={selector.clearAll}
        />
      )}
    </VStack>
  );
}
